/*
 * Mandat de domiciliation SEPA d'un particulier consommateur (fiche CRM).
 * Statut, envoi du mandat seul (membre déjà actif), réception du mandat signé, révocation.
 * Le mandat envoyé avec le contrat devient actif au passage en « Contrat signé ».
 */
import { type ElementType, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Ban, CheckCircle2, Download, FileText, Landmark, Loader2, Send, Upload } from 'lucide-react';
import type { CrmContact, CrmContactDraft, SharingOperation } from './types';
import { MANDATE_STATE_LABELS, mandateState } from './types';
import { downloadBase64Pdf, generateContract, openStoredFile, sendMandateEmail, uploadSignedMandate } from './api';
import { ibanError, isValidBic, maskIban } from '../../../supabase/functions/_shared/sepa';
import { BILLING_CHANNEL_LABELS, resolveBillingChannel } from '../../../supabase/functions/_shared/crmTodo';
import { errorMessage, formatDateFr, inputClass } from './ui';
import { todayIso } from './validation';

interface Props {
  contact: CrmContact;
  operations: SharingOperation[];
  /** Fiche modifiée non enregistrée : les actions sont bloquées (le serveur lit la fiche enregistrée). */
  dirty: boolean;
  onPatched: (patch: CrmContactDraft, message: string) => Promise<void>;
}

const STATE_STYLES: Record<string, string> = {
  none: 'bg-neutral-100 text-neutral-600',
  pending: 'bg-amber-100 text-amber-800',
  active: 'bg-emerald-100 text-emerald-800',
  revoked: 'bg-red-100 text-red-700',
};

export function MandateSection({ contact: c, operations, dirty, onPatched }: Props) {
  const state = mandateState(c);
  const [busy, setBusy] = useState<string | null>(null);
  const [form, setForm] = useState<'signed' | 'revoke' | null>(null);
  const [signedDate, setSignedDate] = useState(todayIso());
  const [signedFile, setSignedFile] = useState<File | null>(null);
  const [revokedDate, setRevokedDate] = useState(todayIso());

  const bankProblems = [
    ibanError(c.iban),
    !c.account_holder?.trim() ? 'titulaire du compte manquant' : null,
    !isValidBic(c.bic) ? 'BIC manquant ou invalide' : null,
  ].filter((v): v is string => !!v);

  const run = async (key: string, fn: () => Promise<void>) => {
    if (dirty) { toast.error('Enregistrez d’abord la fiche'); return; }
    setBusy(key);
    try {
      await fn();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const preview = () => run('preview', async () => {
    if (bankProblems.length > 0) throw new Error(`Mandat SEPA : ${bankProblems.join(', ')}`);
    const doc = await generateContract(c.id, { document: 'mandate' });
    downloadBase64Pdf(doc.pdfBase64, doc.fileName);
    doc.warnings?.forEach(w => toast(w, { duration: 8000 }));
  });

  const send = () => run('send', async () => {
    if (!c.email) throw new Error('Pas d’adresse e-mail dans la fiche');
    if (bankProblems.length > 0) throw new Error(`Mandat SEPA : ${bankProblems.join(', ')}`);
    const again = state === 'pending';
    const question = again
      ? `Renvoyer le mandat SEPA ${c.mandate_reference} à ${c.email} ?`
      : `Envoyer le mandat SEPA à ${c.email} ?${state === 'revoked' ? ' Une nouvelle référence sera attribuée.' : ''}`;
    if (!window.confirm(question)) return;
    const reference = await sendMandateEmail(c, operations);
    await onPatched(
      { mandate_reference: reference, mandate_sent_at: new Date().toISOString(), mandate_status: null, mandate_signed_at: null, mandate_revoked_at: null, signed_mandate_path: null },
      `Mandat SEPA ${reference} envoyé à ${c.email}`,
    );
  });

  const markSigned = () => run('signed', async () => {
    if (!signedFile) throw new Error('Joignez le mandat signé (PDF)');
    if (!signedDate) throw new Error('Indiquez la date de signature');
    const path = await uploadSignedMandate(c.id, signedFile);
    // Mandat envoyé seul : le canal de facturation reste celui d'avant (sinon le canal automatique passerait en domiciliation).
    const keep = c.billing_channel ?? resolveBillingChannel({ ...c, mandate_status: null }, { companyNumber: c.company_number });
    await onPatched(
      { mandate_status: 'active', mandate_signed_at: signedDate, signed_mandate_path: path, billing_channel: keep },
      `Mandat ${c.mandate_reference} actif · factures : ${BILLING_CHANNEL_LABELS[keep].toLowerCase()}`,
    );
    setForm(null);
    setSignedFile(null);
  });

  const revoke = () => run('revoke', async () => {
    if (!revokedDate) throw new Error('Indiquez la date de révocation');
    const wasDirectDebit = c.billing_channel === 'direct_debit';
    if (!window.confirm(`Révoquer le mandat ${c.mandate_reference} ? Plus aucun prélèvement ne pourra être fait avec ce mandat.${wasDirectDebit ? ' Les factures repasseront par e-mail.' : ''}`)) return;
    // Plus de domiciliation sans mandat actif (contrainte crm_contacts_direct_debit_check) : retour au canal automatique.
    await onPatched(
      { mandate_status: 'revoked', mandate_revoked_at: revokedDate, ...(wasDirectDebit ? { billing_channel: null } : {}) },
      `Mandat ${c.mandate_reference} révoqué`,
    );
    setForm(null);
  });

  const icon = (key: string, Icon: ElementType) => (busy === key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icon className="w-3.5 h-3.5" />);
  const btn = 'flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-neutral-200 text-xs text-neutral-700 hover:bg-neutral-50 disabled:opacity-50';

  return (
    <div className="p-3 rounded-xl border border-neutral-200 space-y-2.5">
      <div className="flex items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 text-xs font-semibold text-neutral-800"><Landmark className="w-4 h-4 text-neutral-500" />Mandat SEPA</p>
        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${STATE_STYLES[state]}`}>{MANDATE_STATE_LABELS[state]}</span>
      </div>
      {state !== 'none' && (
        <p className="text-[11px] text-neutral-600">
          Référence <span className="font-mono text-neutral-900">{c.mandate_reference}</span>
          {c.mandate_sent_at && <> · envoyé le {formatDateFr(c.mandate_sent_at)}</>}
          {c.mandate_signed_at && <> · signé le {formatDateFr(c.mandate_signed_at)}</>}
          {c.mandate_revoked_at && <> · révoqué le {formatDateFr(c.mandate_revoked_at)}</>}
        </p>
      )}
      {state === 'active' && <p className="text-[11px] text-neutral-500">Compte prélevé : {maskIban(c.iban)} · {c.account_holder} · BIC {c.bic}</p>}
      {state !== 'active' && bankProblems.length > 0 && (
        <p className="text-[11px] text-amber-800">Avant l’envoi du mandat : {bankProblems.join(', ')}.</p>
      )}

      <div className="flex flex-wrap gap-2">
        {state !== 'active' && (
          <>
            <button onClick={send} disabled={!!busy} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-neutral-900 text-white text-xs font-semibold hover:bg-neutral-700 disabled:opacity-50">
              {icon('send', Send)}{state === 'pending' ? 'Renvoyer le mandat SEPA' : 'Envoyer le mandat SEPA'}
            </button>
            <button onClick={preview} disabled={!!busy} className={btn}>{icon('preview', Download)}Aperçu du mandat</button>
          </>
        )}
        {state === 'pending' && (
          <button onClick={() => setForm(form === 'signed' ? null : 'signed')} disabled={!!busy} className={btn}><CheckCircle2 className="w-3.5 h-3.5" />Mandat signé reçu</button>
        )}
        {c.signed_mandate_path && (
          <button onClick={() => run('view', () => openStoredFile('crm-documents', String(c.signed_mandate_path)))}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-emerald-200 bg-emerald-50 text-xs text-emerald-700 hover:bg-emerald-100">
            <FileText className="w-3.5 h-3.5" />Voir le mandat signé
          </button>
        )}
        {state === 'active' && (
          <button onClick={() => setForm(form === 'revoke' ? null : 'revoke')} disabled={!!busy}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-200 text-xs text-red-700 hover:bg-red-50 disabled:opacity-50">
            <Ban className="w-3.5 h-3.5" />Révoquer le mandat
          </button>
        )}
      </div>

      {form === 'signed' && (
        <div className="grid grid-cols-2 gap-2 p-2.5 rounded-lg bg-neutral-50 border border-neutral-200">
          <label className="block">
            <span className="block text-[11px] text-neutral-600 mb-1">Date de signature</span>
            <input type="date" value={signedDate} onChange={e => setSignedDate(e.target.value)} className={`${inputClass} border-neutral-200`} />
          </label>
          <label className="block">
            <span className="block text-[11px] text-neutral-600 mb-1">Mandat signé (PDF)</span>
            <input type="file" accept="application/pdf" onChange={e => setSignedFile(e.target.files?.[0] ?? null)} className="block w-full text-[11px] text-neutral-600 mt-2" />
          </label>
          <button onClick={markSigned} disabled={!!busy || !signedFile} className="col-span-2 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#F8BD3D] hover:bg-[#E5A935] text-xs font-semibold text-neutral-900 disabled:opacity-50">
            {icon('signed', Upload)}Enregistrer le mandat signé (statut actif)
          </button>
          <p className="col-span-2 text-[10px] text-neutral-500">Le canal de facturation ne change pas automatiquement : passez-le en Domiciliation dans la fiche si besoin.</p>
        </div>
      )}

      {form === 'revoke' && (
        <div className="flex items-end gap-2 p-2.5 rounded-lg bg-red-50/50 border border-red-200">
          <label className="block flex-1">
            <span className="block text-[11px] text-neutral-600 mb-1">Date de révocation</span>
            <input type="date" value={revokedDate} onChange={e => setRevokedDate(e.target.value)} className={`${inputClass} border-neutral-200`} />
          </label>
          <button onClick={revoke} disabled={!!busy} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-red-600 text-white text-xs font-semibold hover:bg-red-700 disabled:opacity-50">
            {icon('revoke', Ban)}Confirmer la révocation
          </button>
        </div>
      )}
      <p className="text-[10px] text-neutral-400">
        Un mandat révoqué n’est jamais réutilisé : un nouvel envoi reçoit une nouvelle référence. Tant que le mandat est actif, le compte ne se modifie pas :
        révoquez-le puis envoyez un nouveau mandat.
      </p>
    </div>
  );
}
